import {getProjectionDef} from '@apps-common/geocorn-sdk/api/geometry.js';
import importer, {projTransform} from '@apps-common/importer';
import {bbox} from '@turf/bbox';
import {containsExtent} from 'ol/extent.js';
import {EXTENT as WORLD_EXTENT} from 'ol/proj/epsg4326.js';

export const MAX_ASYNC_FILE_SIZE_BYTES = 300000000; // ~300MB

// used for local processing
export const MAX_LOCAL_FILE_SIZE_BYTES = 5000000; // ~5MB
export const MAX_FEATURES = 5000;

const SHAPEFILE_EXTENSIONS = ['shp', 'shx', 'dbf', 'prj', 'cpg'];

export const getExtension = path => path?.toLowerCase().split('.').pop();

const fileGroupIsValid = group => {
  if (group.isShapefile) {
    return !!group.files.find(file => getExtension(file.path) === 'shp');
  }

  return true;
};

export const groupFiles = ({incoming, existing = []}) => {
  // combine existing and incoming files
  const allFiles = [...existing.flatMap(group => group.files), ...incoming];

  const groupIdx = {};
  const groups = [];
  const filesSeen = {};

  allFiles.forEach(file => {
    if (filesSeen[file.name]) {
      // duplicate
      return;
    }

    const parts = file.name.split('.');
    const ext = parts.pop();
    const isShapefile = SHAPEFILE_EXTENSIONS.includes(ext);
    const groupName = parts.join(','); // rebuild after popping extension

    let currentGroupIndex = groupIdx[groupName];

    // create the group if it doesn't exist
    if (currentGroupIndex === undefined) {
      currentGroupIndex = groups.length;
      groupIdx[groupName] = currentGroupIndex;

      groups.push({
        name: groupName,
        files: [],
        isShapefile,
        label: `${groupName}.${ext}`,
      });
    }

    const group = groups[currentGroupIndex];

    // Add the file to the group
    group.files.push(file);
    group.isValid = fileGroupIsValid(group);
    group.size = (group.size || 0) + file.size;

    // Update the label for shapefiles
    if (group.files.length > 1 && group.isShapefile) {
      group.label = `${groupName}.shp`;
    }

    groups[currentGroupIndex] = group;
    filesSeen[file.name] = true;
  });

  return groups;
};

export const getBuiltInProjections = code => {
  return new Promise((resolve, reject) => {
    if (code === '3857') {
      // web mercator
      resolve(
        '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs',
      );
    } else if (
      code.length === 5 &&
      (code.substring(0, 3) === '326' || code.substring(0, 3) === '327')
    ) {
      // support WGS84 UTM
      const hemisphere = code.substring(2, 3) === '6' ? 'north' : 'south';
      const zone = code.substring(3);
      resolve(
        `+proj=utm +zone=${zone} +${hemisphere} +datum=WGS84 +units=m +no_defs +type=crs`,
      );
    } else {
      // attempt to fetch the definition from Granero
      getProjectionDef(code)
        .then(def => {
          resolve(def.proj4);
        })
        .catch(() => {
          reject(new Error('Failed to find a valid projection'));
        });
    }
  });
};

export const processProjection = getProjection => collection => {
  // A bit of a malformed GeoJSON file. The real standard does not allow for
  // this but an earlier version did. This tries to parse the EPSG code but
  // will otherwise reject the file.
  if (collection.crs) {
    // This is likely a GeoJSON or a EWKT that came in with an EPSG code.
    // Importer does not handle that directly.
    const crs = collection.crs;
    if (crs.type === 'name' && crs.properties && crs.properties.name) {
      const name = crs.properties.name;
      if (name.indexOf('EPSG') >= 0) {
        // The CRS code can come in as EPSG:4326 OR as EPSG::4326, using just
        // ':' and using pop() ensures the number is returned.
        const code = name.split(':').pop();

        // Do not do any work if the projection is already correct.
        if (code === '4326') {
          return collection;
        }

        return getProjection(code)
          .then(prjText => projTransform(prjText, collection))
          .catch(() => {
            // The projection is bad/unknown
            throw new Error('Malformed GeoJSON carrying an unknown CRS');
          });
      } else if (name.indexOf('CRS84') >= 0) {
        // This is the "standard" WGS84 projection, no reprojection needed.
        return collection;
      }
    }

    // The projection is bad/unknown
    throw new Error('Malformed GeoJSON carrying an unknown CRS');
  } else {
    // The projection step was only offering WGS84 and Web Mercator, assume
    // that if the collection of features is within the WGS84 boundaries, it is
    // WGS84, otherwise, reproject.
    const collectionBBox = bbox(collection);

    // Check a "buffered" version of world extent because sometimes a point
    // will be errantly across the dateline.
    if (!containsExtent([-300, -120, 300, 120], collectionBBox)) {
      // Assume this is from web mercator.
      return getProjection('3857').then(prjText =>
        projTransform(prjText, collection),
      );
    }
  }

  // No projection hint, no worry.
  return collection;
};

export const checkCollectionExtent = collection => {
  const collectionBBox = bbox(collection);

  if (!containsExtent(WORLD_EXTENT, collectionBBox)) {
    throw new Error(
      'Your data contains coordinates outside of the world! All coordinates should be between -180.0 - 180.0 longitude and -90 - 90 latitude.',
    );
  }
};

// Filter out the features that do not work with Explorer
export const filterInvalidFeatures = features =>
  features
    .filter(
      feature =>
        feature.geometry &&
        (feature.geometry.type === 'Polygon' ||
          feature.geometry.type === 'MultiPolygon') &&
        // The importer does not reject features, and occasionally will let
        // some through without a geometry.
        feature.geometry.coordinates?.length > 0,
    )
    .slice(0, MAX_FEATURES);

export const finalizeImport = collection => {
  const features = filterInvalidFeatures(collection.features);
  let maxFeaturesWarning = '';
  const errors = [];
  const warnings = [];

  // Check collection extent with the filtered features list, since bbox()
  // freaks out if geometry is missing or has an invalid type, etc.
  checkCollectionExtent({...collection, features});

  // This warning message is keyed to the original count of features, not the
  // filtered list.
  if (collection.features.length > MAX_FEATURES) {
    maxFeaturesWarning = `Feature count of ${MAX_FEATURES} reached. Additional features will not be imported, use Features API or import in a separate file.`;
  }

  if (features.length === 0) {
    errors.push('No valid features found.');
  } else if (collection.importer) {
    const hasMultipleDatasets = collection.importer.datasetCount > 1;
    const hasNonPolygonalFeatures =
      collection.importer.geometryTypes.filter(
        type => type.indexOf('Polygon') < 0,
      ).length > 0;

    if (hasMultipleDatasets) {
      warnings.push(
        'Imported file(s) contained multiple datasets, only features from the first are included.',
      );
    }

    if (hasNonPolygonalFeatures) {
      warnings.push(
        'Non-polygon features found. They have been excluded from the results.',
      );
    }
  }

  return {
    errors,
    features,
    maxFeaturesWarning,
    totalCount: collection.features.length,
    warnings,
  };
};

export const importFeatures = (files, getProjection = getBuiltInProjections) =>
  importer(files).then(processProjection(getProjection)).then(finalizeImport);
